/**
 * Templates — listagem de templates Cloud API com filtros e busca.
 *
 * Por enquanto: read + sync + send-test (CRUD completo depende de
 * backend Phase 4). Botões de criar/deletar visíveis com indicação
 * "em breve" para sinalizar próximo passo.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import {
  IconRefresh,
  IconSearch,
  IconSend,
  IconPlus,
  IconTrash,
  IconEdit,
  IconEye,
  IconChevronLeft,
  IconChevronRight,
  IconArrowLeft,
  IconInfoCircle,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import WhatsAppCloudApiService from '../../../../../WhatsAppCloudApi/services/WhatsAppCloudApiService';
import TemplateCreator from './Templates/TemplateCreator';
import TemplatePreview from './Templates/TemplatePreview';
import UpdateCategoryModal from './Templates/UpdateCategoryModal';
import TemplateInfoModal from './Templates/TemplateInfoModal';
import './Templates/templates.css';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = ['all', 'APPROVED', 'PENDING', 'REJECTED', 'PAUSED', 'DISABLED'];
const CATEGORY_OPTIONS = ['all', 'MARKETING', 'UTILITY', 'AUTHENTICATION'];

const Templates = ({ agentId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Sub-views: 'list' (default), 'create' (wizard)
  const [view, setView] = useState('list');
  // Preview modal
  const [previewTemplate, setPreviewTemplate] = useState(null);
  // Update category modal
  const [editCategoryTemplate, setEditCategoryTemplate] = useState(null);
  // Info modal — guia de variáveis
  const [showInfoModal, setShowInfoModal] = useState(false);

  const fetchTemplates = useCallback(
    async (targetPage = 1) => {
      setLoading(true);
      try {
        const resp = await WhatsAppCloudApiService.getTemplates(agentId, {
          page: targetPage,
          pageSize: PAGE_SIZE,
          status: statusFilter,
          category: categoryFilter,
          search: search || undefined,
        });
        const data = resp.data || {};
        setTemplates(data.results || []);
        setCount(data.count || 0);
        setHasNext(!!data.next);
        setHasPrev(!!data.previous);
        setPage(targetPage);
      } catch (err) {
        console.error('[CloudApi] Erro ao buscar templates:', err);
        setTemplates([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    },
    [agentId, statusFilter, categoryFilter, search],
  );

  // Re-fetch quando filtros mudam — debounce no search para não disparar a cada tecla
  useEffect(() => {
    const handle = setTimeout(() => fetchTemplates(1), search ? 350 : 0);
    return () => clearTimeout(handle);
  }, [fetchTemplates, search]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const resp = await WhatsAppCloudApiService.syncTemplates(agentId);
      Swal.fire({
        icon: 'success',
        title: `${resp.data?.synced || 0} templates sincronizados`,
        timer: 2000,
        showConfirmButton: false,
      });
      await fetchTemplates(1);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao sincronizar' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSendTest = async (template) => {
    const { value: phone } = await Swal.fire({
      title: `Enviar "${template.name}"`,
      input: 'text',
      inputLabel: 'Número do destinatário (com DDD)',
      inputPlaceholder: '5511999999999',
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
      inputValidator: (val) => {
        if (!val || val.replace(/\D/g, '').length < 10) {
          return 'Informe um número válido.';
        }
      },
    });

    if (!phone) return;

    try {
      await WhatsAppCloudApiService.sendTemplate(agentId, {
        phone: phone.replace(/\D/g, ''),
        template_name: template.name,
        language: template.language,
        parameters: [],
      });
      Swal.fire({
        icon: 'success',
        title: 'Template enviado!',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao enviar',
        text: err.response?.data?.error || 'Tente novamente.',
      });
    }
  };

  const handleDelete = async (template) => {
    const confirm = await Swal.fire({
      title: `Excluir "${template.name}"?`,
      html: `
        <div style="text-align:left;font-size:13px">
          <p>Esta ação irá:</p>
          <ul style="margin:8px 0;padding-left:20px">
            <li>Remover <strong>todas as línguas</strong> do template "${template.name}" da Meta</li>
            <li>Excluir do banco local</li>
          </ul>
          <p style="color:#dc2626;margin-top:8px">
            Não pode ser desfeito. Mensagens já enviadas continuam funcionando.
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;

    try {
      await WhatsAppCloudApiService.deleteTemplate(agentId, template.name);
      Swal.fire({
        icon: 'success',
        title: 'Template excluído',
        timer: 1500,
        showConfirmButton: false,
      });
      await fetchTemplates(page);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao excluir',
        text: err.response?.data?.error || err.message || 'Tente novamente.',
      });
    }
  };

  const handleCreate = () => {
    setView('create');
  };

  const handleCreated = useCallback(async () => {
    setView('list');
    await fetchTemplates(1);
  }, [fetchTemplates]);

  // Filtros agora são server-side — `templates` já vem filtrado.
  const filtered = templates;

  const getBodyText = (template) => {
    const body = (template.components || []).find((c) => c.type === 'BODY');
    return body?.text || '';
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // Sub-view: wizard de criação
  if (view === 'create') {
    return (
      <div>
        <button
          type="button"
          onClick={() => setView('list')}
          className="cloudapi-hero__action-btn"
          style={{ marginBottom: 12 }}
        >
          <IconArrowLeft size={14} />
          Voltar para lista
        </button>
        <TemplateCreator
          agentId={agentId}
          onCancel={() => setView('list')}
          onCreated={handleCreated}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="cloudapi-template-toolbar">
        <div className="cloudapi-template-toolbar__search">
          <IconSearch
            size={14}
            className="cloudapi-template-toolbar__search-icon"
          />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              Status: {s === 'all' ? 'todos' : s}
            </option>
          ))}
        </select>

        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              Categoria: {c === 'all' ? 'todas' : c}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setShowInfoModal(true)}
          className="cloudapi-hero__action-btn"
          style={{ marginLeft: 'auto' }}
          title="Como funcionam variáveis em templates"
        >
          <IconInfoCircle size={14} />
          Informações
        </button>

        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="cloudapi-hero__action-btn"
        >
          {syncing ? <Spinner animation="border" size="sm" /> : <IconRefresh size={14} />}
          Sincronizar
        </button>

        <button type="button" onClick={handleCreate} className="cloudapi-hero__action-btn">
          <IconPlus size={14} />
          Novo
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Spinner animation="border" size="sm" />
        </div>
      ) : filtered.length === 0 ? (
        <p style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          Nenhum template encontrado.
        </p>
      ) : (
        <>
          {filtered.map((tmpl) => (
            <div key={tmpl.id} className="cloudapi-template-card">
              <div className="cloudapi-template-card__header">
                <span className={`cloudapi-template-status cloudapi-template-status--${tmpl.status}`}>
                  {tmpl.status}
                </span>
                <span className="cloudapi-template-card__name">{tmpl.name}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{tmpl.language}</span>
                <span style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase' }}>
                  {tmpl.category}
                </span>
                <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                  <button
                    type="button"
                    onClick={() => setPreviewTemplate(tmpl)}
                    title="Pré-visualizar"
                    className="cloudapi-hero__action-btn"
                    style={{ padding: 6 }}
                  >
                    <IconEye size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditCategoryTemplate(tmpl)}
                    title="Alterar categoria"
                    className="cloudapi-hero__action-btn"
                    style={{ padding: 6 }}
                    disabled={tmpl.status !== 'APPROVED'}
                  >
                    <IconEdit size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSendTest(tmpl)}
                    title="Enviar teste"
                    className="cloudapi-hero__action-btn"
                    style={{ padding: 6 }}
                  >
                    <IconSend size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(tmpl)}
                    title="Excluir"
                    className="cloudapi-hero__action-btn cloudapi-hero__action-btn--danger"
                    style={{ padding: 6 }}
                  >
                    <IconTrash size={12} />
                  </button>
                </div>
              </div>
              {getBodyText(tmpl) && (
                <p className="cloudapi-template-card__body">{getBodyText(tmpl)}</p>
              )}
            </div>
          ))}

          {(hasPrev || hasNext) && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 16,
              }}
            >
              <button
                type="button"
                onClick={() => fetchTemplates(page - 1)}
                disabled={!hasPrev || loading}
                className="cloudapi-hero__action-btn"
              >
                <IconChevronLeft size={12} />
                Anterior
              </button>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Página {page} de {totalPages} · {count} templates
              </span>
              <button
                type="button"
                onClick={() => fetchTemplates(page + 1)}
                disabled={!hasNext || loading}
                className="cloudapi-hero__action-btn"
              >
                Próxima
                <IconChevronRight size={12} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview modal */}
      <Modal
        show={!!previewTemplate}
        onHide={() => setPreviewTemplate(null)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 14 }}>
            {previewTemplate?.name}{' '}
            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
              ({previewTemplate?.language})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewTemplate && <TemplatePreview template={previewTemplate} />}
        </Modal.Body>
      </Modal>

      {/* Update category modal */}
      <UpdateCategoryModal
        show={!!editCategoryTemplate}
        template={editCategoryTemplate}
        agentId={agentId}
        onHide={() => setEditCategoryTemplate(null)}
        onUpdated={() => fetchTemplates(page)}
      />

      {/* Info modal — guia de variáveis */}
      <TemplateInfoModal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
      />
    </div>
  );
};

export default Templates;
